<template>
  <div>
    <v-dialog v-model="showWelcome" max-width="500" persistent>
      <v-card class="rounded-xl pa-2" style="overflow:hidden;">
        <div style="background:linear-gradient(135deg,#6366f1,#f97316);height:5px;border-radius:8px 8px 0 0;" />
        <v-card-text class="pt-6 pb-2 text-center">
          <div class="mb-3" style="font-size:48px;line-height:1;">🚀</div>
          <div class="text-h6 font-weight-bold mb-1">Welcome to Employee Growth Tracker</div>
          <div class="body-2 grey--text mb-4">Your personal dashboard to track performance, habits, and growth — all in one place.</div>
          <v-row dense class="mb-2">
            <v-col v-for="card in cardPreviews" :key="card.key" cols="6">
              <div class="rounded-lg pa-3 text-left" style="background:rgba(99,102,241,0.06);border:1px solid rgba(99,102,241,0.12);">
                <div class="d-flex align-center mb-1" style="gap:7px;">
                  <span style="font-size:18px;">{{ card.icon }}</span>
                  <span class="caption font-weight-bold">{{ card.name }}</span>
                </div>
                <div class="grey--text" style="font-size:10px;line-height:1.4;">{{ card.desc }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 d-flex flex-column" style="gap:8px;">
          <v-btn block depressed color="primary" class="white--text rounded-lg text-capitalize font-weight-bold" large @click="startTour">
            <v-icon small class="mr-2">mdi-map-marker-path</v-icon>Take the Quick Tour
          </v-btn>
          <v-btn block text class="text-capitalize grey--text" @click="skipTour">Skip, I'll explore on my own</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <transition name="fade">
      <div v-if="showStep" class="tour-overlay" />
    </transition>

    <transition name="fade">
      <div
        v-if="showStep && spotlight"
        class="tour-spotlight"
        :style="{
          top:    spotlight.top    + 'px',
          left:   spotlight.left  + 'px',
          width:  spotlight.width + 'px',
          height: spotlight.height + 'px',
          '--glow': currentCard.dotColor,
        }"
      />
    </transition>

    <transition name="slide-panel">
      <div
        v-if="showStep"
        class="tour-panel"
        :style="{ top: panelY + 'px', right: panelX + 'px' }"
        ref="panel"
      >
        <div class="tour-panel-handle" @mousedown="startDrag" @touchstart.prevent="startDragTouch">
          <div class="d-flex align-center justify-space-between px-3 py-2">
            <div class="d-flex align-center" style="gap:6px;">
              <v-icon size="14" color="white" style="opacity:.6;">mdi-drag-horizontal-variant</v-icon>
              <span style="font-size:10px;color:rgba(255,255,255,0.75);font-weight:700;letter-spacing:.06em;">GROWTH TOUR</span>
            </div>
            <v-btn icon x-small @click="endTour(false)" style="color:rgba(255,255,255,0.6);">
              <v-icon size="15">mdi-close</v-icon>
            </v-btn>
          </div>
          <div :style="{ background: currentCard.color, height: '3px' }" />
        </div>

        <div class="tour-panel-body">
          <div class="d-flex justify-center mb-3" style="gap:5px;">
            <div
              v-for="(c, i) in tourCards"
              :key="i"
              :style="{
                width:  i === stepIndex ? '18px' : '5px',
                height: '5px', borderRadius: '9999px',
                background: i < stepIndex ? '#22c55e' : i === stepIndex ? currentCard.dotColor : 'rgba(128,128,128,0.2)',
                transition: 'all .3s',
              }"
            />
          </div>

          <div class="text-center mb-3">
            <div style="font-size:36px;line-height:1.2;">{{ currentCard.icon }}</div>
            <div class="subtitle-2 font-weight-bold mt-1">{{ currentCard.name }}</div>
            <div class="caption grey--text">Step {{ stepIndex + 1 }} of {{ tourCards.length }}</div>
          </div>

          <div class="body-2" style="line-height:1.75;" v-html="currentCard.detail" />
        </div>

        <div class="tour-panel-footer">
          <v-btn text x-small class="text-capitalize grey--text" :disabled="stepIndex === 0" @click="prevStep">
            <v-icon x-small>mdi-chevron-left</v-icon> Back
          </v-btn>
          <v-btn text x-small class="text-capitalize grey--text" @click="endTour(false)">Skip</v-btn>
          <v-btn depressed small :color="currentCard.btnColor" dark class="rounded-lg text-capitalize font-weight-bold" @click="nextStep">
            {{ stepIndex === tourCards.length - 1 ? 'Finish 🎉' : 'Next' }}
            <v-icon x-small class="ml-1" v-if="stepIndex < tourCards.length - 1">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </transition>

    <v-snackbar v-model="showCompletedSnack" timeout="5000" bottom right color="success" rounded="lg">
      🎉 Tour complete! Click <strong>Tour</strong> in the header to replay.
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showCompletedSnack = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
const STORAGE_KEY = 'growth_onboarding_done_v1';

const TOUR_CARDS = [
  { key: 'role-profile', icon: '🪪', name: 'Role Profile', color: 'linear-gradient(90deg,#6366f1,#8b5cf6)', dotColor: '#6366f1', btnColor: 'indigo', desc: 'Your role summary', detail: 'Shows your <strong>current role, department, and growth profile</strong>. Your profile determines how your score is weighted.', tourAttr: 'growth-role-profile' },
  { key: 'kpi', icon: '📊', name: 'KPI Tracker', color: 'linear-gradient(90deg,#0ea5e9,#6366f1)', dotColor: '#0ea5e9', btnColor: 'blue darken-1', desc: 'Daily KPI checkboxes', detail: 'Check off your <strong>daily Key Performance Indicators</strong> to earn growth points. KPIs reset every day.', tourAttr: 'growth-kpi' },
  { key: 'areas', icon: '🌱', name: 'Growth Areas', color: 'linear-gradient(90deg,#22c55e,#16a34a)', dotColor: '#22c55e', btnColor: 'green darken-1', desc: 'Skills to develop', detail: 'Track <strong>focus areas for personal development</strong> — skills, behaviours, and mindsets.', tourAttr: 'growth-areas' },
  { key: 'focus', icon: '🎯', name: "Today's Focus", color: 'linear-gradient(90deg,#f59e0b,#f97316)', dotColor: '#f97316', btnColor: 'orange darken-1', desc: 'Your top priority today', detail: 'Set <strong>one clear intention for the day</strong>. Keeps you laser-focused and helps you avoid context-switching.', tourAttr: 'growth-focus' },
  { key: 'ai', icon: '🤖', name: 'AI Growth Coach', color: 'linear-gradient(90deg,#8b5cf6,#6366f1)', dotColor: '#8b5cf6', btnColor: 'deep-purple', desc: 'Personalised AI advice', detail: 'Get <strong>AI-generated coaching insights</strong> based on your score, habits, and KPIs.', tourAttr: 'growth-ai' },
  { key: 'habits', icon: '🔁', name: 'Habit Tracker', color: 'linear-gradient(90deg,#f97316,#f59e0b)', dotColor: '#f97316', btnColor: 'orange', desc: 'Daily recurring habits', detail: '<strong>Build daily habits</strong> that compound over time. Track streaks and your 12-month history.', tourAttr: 'growth-habits' },
  { key: 'feedback-send', icon: '💬', name: 'Send Feedback', color: 'linear-gradient(90deg,#06b6d4,#0ea5e9)', dotColor: '#06b6d4', btnColor: 'cyan darken-1', desc: 'Give peer feedback', detail: 'Send <strong>structured feedback to teammates</strong> — recognise great work or suggest improvements.', tourAttr: 'growth-feedback-send' },
  { key: 'feedback-inbox', icon: '📥', name: 'Feedback Inbox', color: 'linear-gradient(90deg,#10b981,#06b6d4)', dotColor: '#10b981', btnColor: 'teal', desc: 'Receive feedback', detail: 'See all <strong>feedback received from peers and managers</strong>. Identify patterns and celebrate wins.', tourAttr: 'growth-feedback-inbox' },
  { key: 'score', icon: '⚡', name: 'Growth Score', color: 'linear-gradient(90deg,#f59e0b,#ef4444)', dotColor: '#f59e0b', btnColor: 'warning', desc: 'Your performance score', detail: 'Your <strong>overall score</strong> aggregates KPIs, habits, tasks, and feedback into one daily number.', tourAttr: 'growth-score' },
  { key: 'leaderboard', icon: '🏆', name: 'Leaderboard', color: 'linear-gradient(90deg,#f59e0b,#f97316)', dotColor: '#f59e0b', btnColor: 'orange darken-2', desc: 'Monthly rankings', detail: 'See how you rank against your team this month. The <strong>leaderboard refreshes daily</strong> based on growth scores.', tourAttr: 'growth-leaderboard' },
];

export default {
  name: 'EmployeeGrowthOnboarding',
  data() {
    return {
      showWelcome: false,
      showStep: false,
      showCompletedSnack: false,
      stepIndex: 0,
      tourCards: TOUR_CARDS,
      cardPreviews: TOUR_CARDS.slice(0, 6),
      panelX: 16,
      panelY: 80,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartPanelX: 0,
      dragStartPanelY: 0,
      spotlight: null,
    };
  },
  computed: {
    currentCard() {
      return this.tourCards[this.stepIndex] || this.tourCards[0];
    },
  },
  mounted() {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setTimeout(() => { this.showWelcome = true; }, 800);
    }
    this.$root.$on('growth:start-tour', this.replayTour);
    window.addEventListener('mousemove', this.onDragMove);
    window.addEventListener('mouseup', this.stopDrag);
    window.addEventListener('touchmove', this.onDragMoveTouch, { passive: false });
    window.addEventListener('touchend', this.stopDrag);
  },
  beforeDestroy() {
    this.$root.$off('growth:start-tour', this.replayTour);
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.stopDrag);
    window.removeEventListener('touchmove', this.onDragMoveTouch);
    window.removeEventListener('touchend', this.stopDrag);
  },
  methods: {
    startTour() {
      this.showWelcome = false;
      this.stepIndex = 0;
      this.$nextTick(() => { this.showStep = true; this.scrollAndHighlight(); });
    },
    replayTour() {
      this.stepIndex = 0;
      this.showWelcome = false;
      this.showStep = true;
      this.$nextTick(this.scrollAndHighlight);
    },
    skipTour() {
      this.showWelcome = false;
      localStorage.setItem(STORAGE_KEY, '1');
    },
    nextStep() {
      if (this.stepIndex < this.tourCards.length - 1) {
        this.stepIndex++;
        this.scrollAndHighlight();
      } else {
        this.endTour(true);
      }
    },
    prevStep() {
      if (this.stepIndex > 0) {
        this.stepIndex--;
        this.scrollAndHighlight();
      }
    },
    endTour(completed = false) {
      this.showStep = false;
      this.spotlight = null;
      localStorage.setItem(STORAGE_KEY, '1');
      if (completed) this.showCompletedSnack = true;
    },
    scrollAndHighlight() {
      this.$nextTick(() => {
        const el = document.querySelector(`[data-tour="${this.currentCard.tourAttr}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(this.updateSpotlight, 420);
        } else {
          this.updateSpotlight();
        }
      });
    },
    updateSpotlight() {
      const el = document.querySelector(`[data-tour="${this.currentCard.tourAttr}"]`);
      if (!el) { this.spotlight = null; return; }
      const r = el.getBoundingClientRect();
      this.spotlight = {
        top:    r.top    + window.scrollY - 8,
        left:   r.left   + window.scrollX - 8,
        width:  r.width  + 16,
        height: r.height + 16,
      };
    },
    startDrag(e) {
      this.dragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartPanelX = this.panelX;
      this.dragStartPanelY = this.panelY;
    },
    onDragMove(e) {
      if (!this.dragging) return;
      this.panelX = Math.max(0, this.dragStartPanelX - (e.clientX - this.dragStartX));
      this.panelY = Math.max(0, this.dragStartPanelY + (e.clientY - this.dragStartY));
    },
    stopDrag() { this.dragging = false; },
    startDragTouch(e) {
      const t = e.touches[0];
      this.dragging = true;
      this.dragStartX = t.clientX;
      this.dragStartY = t.clientY;
      this.dragStartPanelX = this.panelX;
      this.dragStartPanelY = this.panelY;
    },
    onDragMoveTouch(e) {
      if (!this.dragging) return;
      e.preventDefault();
      const t = e.touches[0];
      this.panelX = Math.max(0, this.dragStartPanelX - (t.clientX - this.dragStartX));
      this.panelY = Math.max(0, this.dragStartPanelY + (t.clientY - this.dragStartY));
    },
  },
};
</script>
<style scoped>
.tour-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 199; pointer-events: none;
}
.tour-spotlight {
  position: absolute; z-index: 200;
  border-radius: 16px; pointer-events: none;
  box-shadow: 0 0 0 5px var(--glow), 0 0 0 9999px rgba(0,0,0,0.5), 0 0 30px 8px var(--glow);
  animation: pulse-glow 1.8s ease-in-out infinite;
}
@keyframes pulse-glow {
  0%,100% { box-shadow: 0 0 0 4px var(--glow), 0 0 0 9999px rgba(0,0,0,0.5), 0 0 20px 5px var(--glow); }
  50%      { box-shadow: 0 0 0 7px var(--glow), 0 0 0 9999px rgba(0,0,0,0.5), 0 0 38px 12px var(--glow); }
}
.tour-panel {
  position: fixed; z-index: 300; width: 295px;
  border-radius: 16px; overflow: hidden; background: #fff;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.15);
}
.tour-panel-handle { background: #0f172a; cursor: grab; user-select: none; }
.tour-panel-handle:active { cursor: grabbing; }
.tour-panel-body { padding: 16px 16px 8px; }
.tour-panel-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px 12px; border-top: 1px solid rgba(128,128,128,0.1);
}
.fade-enter-active, .fade-leave-active { transition: opacity .25s; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-panel-enter-active, .slide-panel-leave-active { transition: opacity .25s, transform .25s; }
.slide-panel-enter, .slide-panel-leave-to { opacity: 0; transform: translateX(40px); }
</style>
